import { memo, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { metricCatalogue } from "@/features/explorer/data/catalogue";
import { BarChart3, ArrowRightLeft, Layers, CheckCircle2, Map, X, Sparkles } from "lucide-react";

type ExplorerWelcomeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onStartTour?: () => void;
};

export const ExplorerWelcomeModal = memo(({ isOpen, onClose, onStartTour }: ExplorerWelcomeModalProps) => {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Global Escape key handler for keyboard accessibility
  useEffect(() => {
    if (!isOpen) return () => {};

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => {
      globalThis.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const metricCount = metricCatalogue.length;

  return (
    <dialog
      open
      aria-modal='true'
      aria-labelledby='welcome-atlas-title'
      className='fixed inset-0 z-50 flex size-full max-h-none max-w-none items-start sm:items-center justify-center border-none bg-transparent p-3 pt-[6vh] sm:p-4 sm:pt-4 font-sans'>
      {/* Explicit interactive backdrop button layer */}
      <button
        type='button'
        onClick={onClose}
        tabIndex={-1}
        aria-label='Close modal backdrop'
        className='fixed inset-0 bg-background/80 backdrop-blur-md transition duration-200 animate-in fade-in-0 cursor-default border-none outline-none'
      />

      {/* Modal Content Card: Compact vertical max-height with 2-column LG layout to prevent scrolling */}
      <div className='relative z-10 max-h-[85vh] sm:max-h-[90vh] lg:max-h-[94vh] w-full max-w-4xl overflow-y-auto overflow-x-hidden rounded-2xl border border-border bg-card p-4 sm:p-6 lg:p-5 text-card-foreground shadow-2xl backdrop-blur-2xl'>
        {/* Close Icon Button */}
        <button
          type='button'
          onClick={onClose}
          className='absolute top-3 right-3 sm:top-4 sm:right-4 cursor-pointer rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          aria-label='Close overview modal'>
          <X className='size-5' />
        </button>

        {/* Hero Header Section */}
        <div className='space-y-1.5 pr-8'>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge
              variant='secondary'
              className='rounded-md border border-primary/20 bg-primary/10 px-2.5 py-0.5 font-sans text-xs font-semibold tracking-wider text-primary uppercase'>
              Official ONS National Data Atlas 🇬🇧
            </Badge>
            <span className='font-mono text-xs text-muted-foreground'>
              {metricCount} Active Metrics • Live PostGIS Data
            </span>
          </div>

          <h1
            id='welcome-atlas-title'
            className='font-sans text-xl sm:text-2xl font-extrabold tracking-tight text-foreground leading-tight'>
            UK National Data Atlas & Quality-of-Life Explorer
          </h1>
          <p className='font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-3xl'>
            An interactive data mapping platform providing visualisations of official ONS statistics across 350 UK Local
            Authorities, 111 Counties, and 11 Macro-Regions.
          </p>
        </div>

        {/* Responsive: 2-column layout on LG laptops (landscape optimized), 1-column on mobile */}
        <div className='mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-stretch'>
          {/* Left Column (LG 7 cols): 4 Feature Highlights */}
          <div className='lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 sm:auto-rows-fr gap-2.5'>
            <div className='rounded-xl border border-border bg-muted/40 p-3 transition hover:border-primary/40'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-lg bg-primary/10 border border-primary/20 p-1.5 text-primary shrink-0'>
                  <BarChart3 className='size-4' />
                </div>
                <h2 className='font-sans text-xs font-bold text-foreground'>{metricCount} Official Datasets</h2>
              </div>
              <p className='mt-1.5 text-xs text-muted-foreground leading-normal'>
                Explore Pay, Savings, Disposable Income, House Prices, Rent Costs, Crime Rates, CO₂ Emissions, and EV
                Density.
              </p>
            </div>

            <div className='rounded-xl border border-border bg-muted/40 p-3 transition hover:border-primary/40'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-lg bg-primary/10 border border-primary/20 p-1.5 text-primary shrink-0'>
                  <Sparkles className='size-4' />
                </div>
                <h2 className='font-sans text-xs font-bold text-foreground'>Quality-of-Life Index</h2>
              </div>
              <p className='mt-1.5 text-xs text-muted-foreground leading-normal'>
                Build custom weighted composite scores (0–100) combining multiple indicators with 4 strategy presets.
              </p>
            </div>

            <div className='rounded-xl border border-border bg-muted/40 p-3 transition hover:border-primary/40'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-lg bg-primary/10 border border-primary/20 p-1.5 text-primary shrink-0'>
                  <ArrowRightLeft className='size-4' />
                </div>
                <h2 className='font-sans text-xs font-bold text-foreground'>Interactive A/B Region Swap</h2>
              </div>
              <p className='mt-1.5 text-xs text-muted-foreground leading-normal'>
                Select any two UK districts to compare local values, absolute differences, and benchmark deltas.
              </p>
            </div>

            <div className='rounded-xl border border-border bg-muted/40 p-3 transition hover:border-primary/40'>
              <div className='flex items-center gap-2.5'>
                <div className='rounded-lg bg-primary/10 border border-primary/20 p-1.5 text-primary shrink-0'>
                  <Layers className='size-4' />
                </div>
                <h2 className='font-sans text-xs font-bold text-foreground'>Multi-Granularity Levels</h2>
              </div>
              <p className='mt-1.5 text-xs text-muted-foreground leading-normal'>
                Toggle between Local Authorities, Counties, and Regions with PostGIS rollups and WebGL choropleth
                coloring.
              </p>
            </div>
          </div>

          {/* Right Column (LG 5 cols): Getting Started Steps - matched to left cards height */}
          <div className='lg:col-span-5 flex flex-col h-full gap-2.5'>
            <div className='flex flex-1 flex-col'>
              <h2 className='font-sans text-xs font-bold text-foreground uppercase tracking-wider mb-2'>
                How to Get Started
              </h2>
              <div className='flex flex-1 flex-col justify-between gap-2 text-xs'>
                <div className='flex flex-1 items-center gap-2.5 rounded-lg border border-border bg-muted/30 p-2.5'>
                  <span className='flex size-4 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-xs'>
                    1
                  </span>
                  <div>
                    <span className='font-semibold text-foreground text-xs block leading-tight'>
                      Select Metric or Index
                    </span>
                    <span className='text-muted-foreground text-xs leading-tight block mt-0.5'>
                      Choose from 17 datasets or adjust composite weights.
                    </span>
                  </div>
                </div>

                <div className='flex flex-1 items-center gap-2.5 rounded-lg border border-border bg-muted/30 p-2.5'>
                  <span className='flex size-4 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-xs'>
                    2
                  </span>
                  <div>
                    <span className='font-semibold text-foreground text-xs block leading-tight'>Hover & Inspect</span>
                    <span className='text-muted-foreground text-xs leading-tight block mt-0.5'>
                      Hover over map boundaries to inspect local statistics.
                    </span>
                  </div>
                </div>

                <div className='flex flex-1 items-center gap-2.5 rounded-lg border border-border bg-muted/30 p-2.5'>
                  <span className='flex size-4 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-xs'>
                    3
                  </span>
                  <div>
                    <span className='font-semibold text-foreground text-xs block leading-tight'>Compare Regions</span>
                    <span className='text-muted-foreground text-xs leading-tight block mt-0.5'>
                      Click 2 regions for side-by-side variance analysis.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA Row: Properly aligned & spaced without horizontal overflow */}
        <div className='mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border/80 pt-4'>
          <div className='flex items-center gap-2 text-xs text-muted-foreground shrink-0'>
            <CheckCircle2 className='size-3.5 text-success shrink-0' />
            <span className='text-xs'>UK Boundaries Ready • 14,136 Observations</span>
          </div>

          <div className='flex w-full sm:w-auto flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0'>
            {onStartTour && (
              <button
                type='button'
                onClick={() => {
                  onClose();
                  onStartTour();
                }}
                className='flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 px-3.5 py-2 font-sans text-xs sm:text-sm font-bold text-primary transition hover:bg-primary/20 cursor-pointer whitespace-nowrap shrink-0'>
                <Sparkles className='size-3.5' />
                Take Guided Tour 🚀
              </button>
            )}

            <button
              type='button'
              onClick={onClose}
              className='flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-5 py-2 font-sans text-xs sm:text-sm font-bold text-primary-foreground shadow-md transition hover:bg-primary/90 cursor-pointer whitespace-nowrap shrink-0'>
              <Map className='size-3.5' />
              Explore Map ➔
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
});

ExplorerWelcomeModal.displayName = "ExplorerWelcomeModal";
